import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
// import dayjs from "dayjs";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import {
  clearSectionOptions,
  fetchSectionOptions,
  fetchStaffClassAndSubjects,
} from "../../../../redux/Masterdata";
import ScheduleLessonPlanContent from "../lessonplanview/ScheduleLessonPlanContent";
import LessonPlanFormPanel from "./LessonPlanFormPanel";
import LessonPlanOutputPanel from "./LessonPlanOutputPanel";

/* ---------------------------------------------------------------------- */
/* Helpers                                                                */
/* ---------------------------------------------------------------------- */

const resolveBookPath = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${IMAGE_BASE_URL.replace(/\/$/, "")}/${String(path).replace(/^\//, "")}`;
};

/* Azure blob book paths look like dev/<clientId>/Upload/Books/x.pdf —
   no protocol, no leading slash. Legacy disk paths start with "/". */
const isBlobBookPath = (path) =>
  !!path &&
  !/^https?:\/\//i.test(path) &&
  !String(path).startsWith("/") &&
  String(path).includes("/Upload/");

const extractSubjectFromFileName = (fileName) => {
  if (!fileName || typeof fileName !== "string") return "";
  const base = fileName.replace(/\.pdf$/i, "");
  const parts = base.split("_");
  if (parts.length >= 3) return parts[1]?.trim() || "";
  return "";
};

const INITIAL_FORM = {
  book: null,
  subject: "",
  class: "",
  medium: "",
  topic: null,
  subTopic: null,
  startPage: "",
  endPage: "",
  sectionOption: null,
};

const INITIAL_MODAL = {
  open: false,
  type: "success",
  message: "",
  redirectPath: "",
};

const MAX_REGENERATE = 3;

const mapStaffBooksToOptions = (rows) =>
  rows.map((item) => ({
    label: `${item.info.class} – ${item.info.subject} – ${item.info.medium}`,
    value: item.info.id,
    info: item.info,
  }));

const mapTopicsToOptions = (topics) =>
  topics.map((row) => ({ label: row.topic, value: row.topic }));

const mapSubTopicsToOptions = (subTopics) =>
  (subTopics || []).map((s) => ({ label: s, value: s }));

const LESSON_STATUS_MAP = {
  0: "",
  1: "submitted",
  2: "approved",
  3: "rejected",
  4: "resubmitted",
  5: "completed",
};

const mapLessonStatus = (rawStatus) => {
  if (rawStatus === null || rawStatus === undefined || rawStatus === "") {
    return "";
  }
  const key = Number(rawStatus);
  if (Number.isNaN(key)) return "";
  return LESSON_STATUS_MAP[key] ?? "";
};

const transformTimetableResponse = (timetableData) => {
  if (!timetableData || !Array.isArray(timetableData.timetable)) return [];
  return timetableData.timetable.map((dayRow) => ({
    day: dayRow.day_name,
    date: moment(dayRow.date).format("MMM - D"),
    rawDate: dayRow.date,
    periods: (dayRow.periods || []).map((p) => {
      const hasClass = p.class_name && p.subject_name;
      return {
        periodId: String(p.period),
        subject: hasClass ? p.subject_name : null,
        section: hasClass
          ? `${p.class_name}${p.section_id ? ` - ${p.section_id}` : ""}`
          : null,
        status: mapLessonStatus(p.status),
        class_id: p.class_id,
        class_name: p.class_name,
        section_id: p.section_id,
        subject_name: p.subject_name,
        course_id: p.course_id,
        period: p.period,
        medium: p.medium ?? "",
      };
    }),
  }));
};

const buildPeriodsFromTimetable = (timetableData) => {
  if (
    !timetableData ||
    !Array.isArray(timetableData.timetable) ||
    timetableData.timetable.length === 0
  ) {
    return [];
  }
  return (timetableData.timetable[0].periods || []).map((p) => ({
    id: String(p.period),
    label: String(p.period),
    time:
      p.break_start_time && p.break_end_time
        ? `${p.break_start_time} - ${p.break_end_time}`
        : "",
  }));
};

const formatYmd = (value) => {
  if (!value) return moment().format("YYYY-MM-DD");
  if (typeof value === "object" && typeof value.format === "function") {
    // dayjs or moment
    if (typeof value?.isValid === "function" && !value.isValid()) {
      return moment().format("YYYY-MM-DD");
    }
    return value.format("YYYY-MM-DD");
  }
  return moment(value).format("YYYY-MM-DD");
};

const normalize = (val) =>
  String(val || "")
    .trim()
    .toUpperCase();

const hasEditorContent = (html) => {
  if (!html) return false;
  const plain = String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&[a-z]+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return plain.length > 0;
};

/* ---------------------------------------------------------------------- */
/* Component                                                              */
/* ---------------------------------------------------------------------- */

const LessonPlanCreate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const editDefId = location.state?.ID || null;
  const editItemId = location.state?.editId || null;
  const editRowData = location.state?.data || null;
  const isEditMode = Boolean(editDefId);
  const fromAllAdd = Boolean(location.state?.fromAllAdd);
  const allAddViewId = location.state?.viewId || null;

  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);

  const { sectionOptions: reduxSectionOptions } = useSelector(
    (state) => state.masterData,
  );

  const user = TokenService.getUser() || {};
  const clientData = user?.client_data?.[0] || {};
  const schoolId =
    clientData.school_id != null && clientData.school_id !== ""
      ? Number(clientData.school_id)
      : null;
  const staffId =
    clientData.master_id != null && clientData.master_id !== ""
      ? Number(clientData.master_id)
      : null;
  const userId = user.id != null ? Number(user.id) : null;
  const academic_id = clientData?.academic_year_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_start_date = clientData?.start_date || "";
  const academic_end_date = clientData?.end_date || "";

  /* -------- Dropdowns / form -------- */
  const [bookOptions, setBookOptions] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);
  const [subTopicOptions, setSubTopicOptions] = useState([]);
  const [topicData, setTopicData] = useState([]);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [pageBounds, setPageBounds] = useState({ min: null, max: null });

  /* -------- Schedule date + timetable -------- */
  const [scheduleDate, setScheduleDate] = useState(null);

  const [timetableRows, setTimetableRows] = useState([]);
  const [timetablePeriods, setTimetablePeriods] = useState([]);
  const [timetableLoading, setTimetableLoading] = useState(false);
  const [selectedCells, setSelectedCells] = useState([]);
  const [cellErrorModal, setCellErrorModal] = useState({
    open: false,
    message: "",
  });

  /* -------- Generated content -------- */
  const [lessonPlanContent, setLessonPlanContent] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [regenerateUsed, setRegenerateUsed] = useState(0);

  /* Blob-aware pdfUrl setter: legacy paths keep the old static URL,
     Azure blob paths are fetched through the authenticated
     /content/image endpoint and shown via an object URL. */
  const applyPdfUrl = useCallback(async (rawPath) => {
    const bookFilePath = rawPath || "";
    if (!bookFilePath) {
      setPdfUrl("");
      return;
    }
    if (!isBlobBookPath(bookFilePath)) {
      setPdfUrl(resolveBookPath(bookFilePath));
      return;
    }
    try {
      const res = await AxiosInstance.get("/content/image", {
        params: { path: bookFilePath },
        responseType: "blob",
      });
      const objectUrl = URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      setPdfUrl(objectUrl);
    } catch (err) {
      console.error("Error loading book PDF from blob:", err);
      setPdfUrl("");
    }
  }, []);
  const [generating, setGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [lessonPlanDefId, setLessonPlanDefId] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [currentItemId, setCurrentItemId] = useState(null);

  /* -------- Save counter (requirement #4) -------- */
  const [saveCount, setSaveCount] = useState(0);

  /* -------- NEW flag: locks both footer buttons the moment a save
     completes; reset whenever a fresh generate/re-generate succeeds. ----- */
  const [saveCompleted, setSaveCompleted] = useState(false);

  /* -------- Saved cards -------- */
  const [savedLessonPlans, setSavedLessonPlans] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [editingCardHmPk, setEditingCardHmPk] = useState(null);

  /* -------- Schedule popup -------- */
  const [schedulePopup, setSchedulePopup] = useState({
    open: false,
    lessonPlan: null,
  });
  const [lessonInfo, setLessonInfo] = useState(null);

  /* -------- Delete confirmation -------- */
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    planId: null,
  });

  /* -------- Modal + submitting -------- */
  const [modalState, setModalState] = useState(INITIAL_MODAL);
  const [submitting, setSubmitting] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);

  /* ------------------------------------------------------------------- */
  /* Derived flags                                                       */
  /* ------------------------------------------------------------------- */

  // Requirement #5: Disable inputs in edit mode OR after generate/regenerate click
  const formLocked = hasGenerated || isEditMode;

  // Requirement #4: After 3 saves, disable save/submit buttons
  const saveLimitReached = saveCount >= MAX_REGENERATE;

  // Generate button: disabled when 3 generates done AND latest has been saved
  const regenerateExhausted = regenerateUsed >= MAX_REGENERATE;

  const contentReady = hasEditorContent(lessonPlanContent);
  const showOutputPanels = Boolean(formData.book) || contentReady;

  const disableScheduleGlobally = useMemo(() => {
    return savedLessonPlans.some((p) => {
      const s = String(p.status || "").toLowerCase();
      return (
        s === "approved" ||
        s === "completed" ||
        s === "submitted" ||
        s === "resubmitted" ||
        s === "re-submitted"
      );
    });
  }, [savedLessonPlans]);

  const sectionDropdownOptions = useMemo(
    () =>
      (reduxSectionOptions || []).map((o) => ({
        value: String(o.value),
        label: String(o.label),
      })),
    [reduxSectionOptions],
  );

  const scheduleLegendItems = useMemo(
    () => [
      { key: "submitted", label: t.legendSubmitted || "Submitted" },
      { key: "approved", label: t.legendApproved || "Approved" },
      { key: "rejected", label: t.legendRejected || "Rejected" },
      { key: "resubmitted", label: t.legendResubmitted || "Re-Submitted" },
      { key: "completed", label: t.legendCompleted || "Completed" },
    ],
    [t],
  );

  const selectedCellsForTable = useMemo(
    () => selectedCells.map((c) => ({ date: c.date, periodId: c.periodId })),
    [selectedCells],
  );

  const lockedSubjectForCells = useMemo(() => {
    return formData.book?.info?.subject || lessonInfo?.subject_name || "";
  }, [formData.book, lessonInfo]);

  const lockedMediumForCells = useMemo(() => {
    return (
      formData.book?.info?.medium || formData.medium || lessonInfo?.medium || ""
    );
  }, [formData.book, formData.medium, lessonInfo]);

  const lockedClassForCells = useMemo(() => {
    const id = formData.book?.info?.class_id || lessonInfo?.class_id || null;
    return id != null ? String(id) : "";
  }, [formData.book, lessonInfo]);

  /* ------------------------------------------------------------------- */
  /* API calls                                                           */
  /* ------------------------------------------------------------------- */

  const loadStaffBooks = useCallback(async () => {
    if (schoolId == null || staffId == null) return;
    try {
      const res = await AxiosInstance.post("/book/getStaffBook", {
        school_id: schoolId,
        staff_id: staffId,
      });
      if (res.data?.status && Array.isArray(res.data.data)) {
        setBookOptions(mapStaffBooksToOptions(res.data.data));
      }
    } catch (err) {
      console.error("Error loading staff books:", err);
    }
  }, [schoolId, staffId]);

  const fetchBookTopic = useCallback(
    async ({ bookId, topic = "", subTopic = "" }) => {
      try {
        const res = await AxiosInstance.post("/book/getBookTopic", {
          school_id: schoolId,
          id: bookId,
          topic,
          sub_topic: subTopic,
          completed_date: 0,
        });
        if (res.data?.status && res.data.data != null) return res.data.data;
        return null;
      } catch (err) {
        console.error("Error fetching book topic:", err);
        return null;
      }
    },
    [schoolId],
  );

  const fetchStaffTimetable = useCallback(
    async (dateValue) => {
      if (!schoolId || !staffId) return;
      const formattedDate = formatYmd(dateValue);
      try {
        setTimetableLoading(true);
        const res = await AxiosInstance.post("/book/getStaffTimetable", {
          school_id: schoolId,
          staff_id: staffId,
          schedule_date: formattedDate,
          academic_year: academic_id,
        });
        if (res.data?.status && res.data.data) {
          setTimetableRows(transformTimetableResponse(res.data.data));
          setTimetablePeriods(buildPeriodsFromTimetable(res.data.data));
        } else {
          setTimetableRows([]);
          setTimetablePeriods([]);
        }
      } catch (err) {
        console.error("Error fetching staff timetable:", err);
        setTimetableRows([]);
        setTimetablePeriods([]);
      } finally {
        setTimetableLoading(false);
      }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    [schoolId, staffId],
  );

  /* ------------------------------------------------------------------- */
  /* VIEW API — used in edit mode AND in fromAllAdd (per-row "+") mode    */
  /* ------------------------------------------------------------------- */

  const fetchLessonPlanView = useCallback(
    async (
      defId,
      {
        populateForm = false,
        preferItemId = null,
        applyGenerateCount = false,
      } = {},
    ) => {
      if (!defId) return;
      try {
        const res = await AxiosInstance.post("/book/lessonPlan/view", {
          id: Number(defId),
          school_id: Number(schoolId),
          academic_id: academic_id,
        });
        if (res.data?.status && res.data.data) {
          const { Lesson_info = [], item_list = [] } = res.data.data;
          const info = Lesson_info[0] || null;

          if (info) {
            setLessonInfo({
              ...info,
              subject_name:
                info.subject_name ||
                extractSubjectFromFileName(info?.pdf_original_name),
            });
          }

          const plans = (item_list || []).map((item) => ({
            id: item.id,
            lesson_plan_id: item.lesson_plan_id,
            version: item.version,
            content: item.content || "",
            start_page: item.start_page,
            end_page: item.end_page,
            created_at: item.created_at,
            title: `${t.lessonPlan || "Lesson Plan"} ${item.version}`,
            date: item.created_at
              ? new Date(item.created_at).toLocaleDateString()
              : "",
            time: item.created_at
              ? new Date(item.created_at).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "",
            status: item.status_label || "Draft",
            hm_pk: item.hm_pk || null,
            schedule_date: item.schedule_date || "",
            selected_periods: Array.isArray(item.selected_periods)
              ? item.selected_periods
              : [],
            is_period: Number(item.is_period) || 0,
            is_pinned: false,
          }));

          setSavedLessonPlans(plans);

          if (populateForm && info) {
            await loadStaffBooks();

            // 👇 Seed the regenerate counter from generate_count.
            //    generate_count === 1 → "Re-Generate 1/3" (2 left)
            //    generate_count === 2 → "Re-Generate 2/3" (1 left)
            //    generate_count === 3 → exhausted (button disabled)
            if (applyGenerateCount) {
              const gc = Number(info.generate_count);
              setRegenerateUsed(Number.isNaN(gc) ? 0 : gc);
            }

            const topicsRes = await fetchBookTopic({ bookId: info.id });
            const topics = Array.isArray(topicsRes) ? topicsRes : [];
            setTopicData(topics);
            setTopicOptions(mapTopicsToOptions(topics));
            const matchedTopic = topics.find((tp) => tp.topic === info.topic);
            if (matchedTopic?.sub_topics) {
              setSubTopicOptions(
                mapSubTopicsToOptions(matchedTopic.sub_topics),
              );
            }

            if (schoolId && staffId && info.class_id) {
              dispatch(
                fetchSectionOptions({
                  id: [parseInt(String(info.class_id), 10)],
                  school_id: parseInt(String(schoolId), 10),
                  staff_id: parseInt(String(staffId), 10),
                  subject_staff: 1,
                }),
              );
            }

            let targetItem = null;
            if (preferItemId != null) {
              targetItem =
                (item_list || []).find(
                  (it) => String(it.id) === String(preferItemId),
                ) || null;
            }
            if (!targetItem) {
              targetItem = (item_list && item_list[0]) || {};
            }

            const subjectName =
              info.subject_name ||
              extractSubjectFromFileName(info.pdf_original_name);

            setFormData({
              book: {
                label: info.book_name,
                value: info.book_id || info.id,
                info: {
                  id: info.book_id || info.id,
                  subject: subjectName,
                  subject_id: info.subject_id || info.subject,
                  class: info.class,
                  class_id: info.class_id,
                  medium: info.medium,
                  filePath: info.uploaded_file_path,
                },
              },
              subject: subjectName || "",
              class: info.class || "",
              medium: info.medium || "",
              topic: info.topic
                ? { label: info.topic, value: info.topic }
                : null,
              subTopic: info.sub_topic
                ? { label: info.sub_topic, value: info.sub_topic }
                : null,
              startPage: targetItem.start_page ?? "",
              endPage: targetItem.end_page ?? "",
              sectionOption:
                targetItem.section_ids || info.section
                  ? {
                      label: String(targetItem.section_ids || info.section),
                      value: String(targetItem.section_ids || info.section),
                    }
                  : null,
            });

            applyPdfUrl(info.uploaded_file_path);
            setLessonPlanContent(targetItem.content || "");
            setCurrentVersion(targetItem.version || null);
            setCurrentItemId(targetItem.id || null);
            setSelectedCardId(targetItem.id || null);
            setLessonPlanDefId(defId);
            setHasGenerated(true);

            if (targetItem.schedule_date) {
              setScheduleDate(new Date(targetItem.schedule_date));
              fetchStaffTimetable(new Date(targetItem.schedule_date));
            } else {
              fetchStaffTimetable(null);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching lesson plan view:", err);
      }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      schoolId,
      staffId,
      t,
      dispatch,
      loadStaffBooks,
      fetchBookTopic,
      fetchStaffTimetable,
    ],
  );

  /* ------------------------------------------------------------------- */
  /* Initial mount                                                       */
  /* ------------------------------------------------------------------- */

  useEffect(() => {
    if (staffId) {
      dispatch(
        fetchStaffClassAndSubjects({
          staff_id: parseInt(String(staffId), 10),
          class_id: "",
          section_id: "",
          subject_id: "",
        }),
      );
    }
  }, [dispatch, staffId]);

  // Fresh-create timetable load. Skipped for edit AND fromAllAdd — those
  // flows let fetchLessonPlanView load the timetable after prefill.
  useEffect(() => {
    if (!isEditMode && !fromAllAdd) fetchStaffTimetable(null);
  }, [fetchStaffTimetable, isEditMode, fromAllAdd]);

  useEffect(() => {
    if (isEditMode) {
      // Edit flow (from card edit / view edit): prefill + edit mode.
      fetchLessonPlanView(editDefId, {
        populateForm: true,
        preferItemId: editItemId,
      });
    } else if (fromAllAdd && allAddViewId) {
      // Per-row "+" flow: prefill via view API + seed regenerate counter,
      // but NOT edit mode (Generate stays available).
      fetchLessonPlanView(allAddViewId, {
        populateForm: true,
        applyGenerateCount: true,
      });
    } else {
      // Plain create / reminder / top ADD button → no view API.
      loadStaffBooks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editDefId, editItemId, fromAllAdd, allAddViewId]);

  /* ------------------------------------------------------------------- */
  /* Form handlers                                                       */
  /* ------------------------------------------------------------------- */

  const resetBookCascade = useCallback(() => {
    setTopicData([]);
    setTopicOptions([]);
    setSubTopicOptions([]);
    setPdfUrl("");
    setPageBounds({ min: null, max: null });
    dispatch(clearSectionOptions());
  }, [dispatch]);

  const handleBookChange = useCallback(
    async (selected) => {
      if (formLocked) return;
      setLessonPlanContent("");
      setSelectedCells([]);

      if (!selected) {
        setFormData(INITIAL_FORM);
        resetBookCascade();
        return;
      }

      const info = selected.info || {};
      // Keep the full info (incl. subject_id) on the option so save payload
      // can later read formData.book.info.subject_id as course_id.
      setFormData((prev) => ({
        ...prev,
        book: {
          ...selected,
          info: {
            ...info,
            // explicit so downstream consumers always find it
            subject_id: info.subject_id ?? info.subject ?? "",
          },
        },
        subject: info.subject || "",
        class: info.class || "",
        medium: info.medium || "",
        topic: null,
        subTopic: null,
        startPage: "",
        endPage: "",
        sectionOption: null,
      }));
      applyPdfUrl(info.filePath);
      setPageBounds({ min: null, max: null });

      if (schoolId && staffId && info.class_id) {
        dispatch(
          fetchSectionOptions({
            id: [parseInt(String(info.class_id), 10)],
            school_id: parseInt(String(schoolId), 10),
            staff_id: parseInt(String(staffId), 10),
            subject_staff: 1,
          }),
        );
      }

      const topicsRes = await fetchBookTopic({ bookId: info.id });
      const topics = Array.isArray(topicsRes) ? topicsRes : [];
      setTopicData(topics);
      setTopicOptions(mapTopicsToOptions(topics));
      setSubTopicOptions([]);
    },
    [dispatch, fetchBookTopic, resetBookCascade, schoolId, staffId, formLocked],
  );

  const handleSectionChange = useCallback(
    (selected) => {
      if (formLocked) return;
      setFormData((prev) => ({ ...prev, sectionOption: selected || null }));
    },
    [formLocked],
  );

  const handleTopicChange = useCallback(
    async (selected) => {
      if (formLocked) return;
      const bookId = formData.book?.value;

      setFormData((prev) => ({
        ...prev,
        topic: selected || null,
        subTopic: null,
        startPage: "",
        endPage: "",
      }));

      if (!selected) {
        setSubTopicOptions([]);
        setPageBounds({ min: null, max: null });
        return;
      }

      const match = topicData.find((row) => row.topic === selected.value);
      setSubTopicOptions(mapSubTopicsToOptions(match?.sub_topics));

      if (!bookId) return;

      const pageRes = await fetchBookTopic({
        bookId,
        topic: selected.value,
      });
      if (pageRes && !Array.isArray(pageRes)) {
        const minP = pageRes.pdf_start_page ?? null;
        const maxP = pageRes.pdf_end_page ?? null;
        setPageBounds({ min: minP, max: maxP });
        setFormData((prev) => ({
          ...prev,
          startPage: minP ?? "",
          endPage: maxP ?? "",
          completedDate: pageRes.completed_date ?? "",
          month: pageRes.month ?? "",
        }));
        if (pageRes.book_path || pageRes.filePath) {
          applyPdfUrl(pageRes.book_path || pageRes.filePath);
        }
      }
    },
    [fetchBookTopic, formData.book?.value, topicData, formLocked],
  );

  const handleSubTopicChange = useCallback(
    async (selected) => {
      if (formLocked) return;
      const topic = formData.topic;
      const book = formData.book;

      setFormData((prev) => ({ ...prev, subTopic: selected || null }));

      if (!selected || !topic || !book) return;

      const res = await fetchBookTopic({
        bookId: book.value,
        topic: topic.value,
        subTopic: selected.value,
      });
      if (res && !Array.isArray(res)) {
        const minP = res.pdf_start_page ?? null;
        const maxP = res.pdf_end_page ?? null;
        setPageBounds({ min: minP, max: maxP });
        setFormData((prev) => ({
          ...prev,
          startPage: minP ?? prev.startPage,
          endPage: maxP ?? prev.endPage,
          completedDate: res.completed_date ?? prev.completedDate,
          month: res.month ?? prev.month,
        }));
        if (res.book_path || res.filePath) {
          applyPdfUrl(res.book_path || res.filePath);
        }
      }
    },
    [fetchBookTopic, formData.topic, formData.book, formLocked],
  );

  const handlePageInputChange = useCallback(
    (e) => {
      if (formLocked) return;
      const { name, value } = e.target;
      const digitsOnly = value.replace(/\D/g, "");
      setFormData((prev) => ({ ...prev, [name]: digitsOnly }));
    },
    [formLocked],
  );

  const handlePageInputBlur = useCallback(
    (e) => {
      if (formLocked) return;
      const { name, value } = e.target;
      if (value === "") return;

      const num = parseInt(value, 10);
      const { min, max } = pageBounds;

      let snapped = num;
      let errorMsg = "";

      if (min != null && num < min) {
        snapped = min;
        errorMsg = `${t.pageValueBetween} ${min} ${t.andLabel} ${max}`;
      } else if (max != null && num > max) {
        snapped = max;
        errorMsg = `${t.pageValueBetween} ${min} ${t.andLabel} ${max}`;
      }

      setFormData((prev) => {
        const next = { ...prev, [name]: String(snapped) };
        const s = parseInt(next.startPage, 10);
        const en = parseInt(next.endPage, 10);
        if (!Number.isNaN(s) && !Number.isNaN(en) && s > en) {
          if (name === "startPage") next.startPage = next.endPage;
          else next.endPage = next.startPage;
        }
        return next;
      });
      setErrors((prev) => ({ ...prev, [name]: errorMsg }));
    },
    [pageBounds, t, formLocked],
  );

  /* Requirement: use dayjs for date */
  const handleScheduleDateChange = useCallback(
    (value) => {
      // value from CustomDatePicker is a dayjs object or null
      let dateObj = null;
      if (value) {
        if (typeof value?.toDate === "function") {
          dateObj = value.toDate();
        } else {
          dateObj = new Date(value);
        }
      }
      setScheduleDate(dateObj);
      setErrors((prev) => ({ ...prev, scheduleDate: "" }));
      setSelectedCells([]);
      fetchStaffTimetable(dateObj);
    },
    [fetchStaffTimetable],
  );

  /* Requirement #2: no cell selection without book
   Requirement #7: only white (empty/no status) cells selectable
   NEW #1: same subject + class + section combo cannot be re-selected
   NEW #2: past dates cannot be selected */
  const handleCellClick = useCallback(
    (cellInfo) => {
      // #2: Block if book is not selected
      if (!formData.book) {
        setCellErrorModal({
          open: true,
          message:
            t.selectBookFirst || "Please select a book before choosing a slot.",
        });
        return;
      }

      // ✅ Block if ANY saved lesson plan in item_list has is_period === 1
      const hasAlreadySelectedPeriod = savedLessonPlans.some(
        (plan) => Number(plan.is_period) === 1,
      );
      if (hasAlreadySelectedPeriod) {
        setCellErrorModal({
          open: true,
          message:
            t.periodAlreadySelected ||
            "You have already selected the period and saved it as draft. You cannot select another cell.",
        });
        return;
      }

      // NEW #2: Block past dates (yesterday and earlier)
      const cellDateStr = cellInfo.rawDate || cellInfo.date;
      const cellDay = moment(cellDateStr).startOf("day");
      const today = moment().startOf("day");
      if (cellDay.isBefore(today)) {
        setCellErrorModal({
          open: true,
          message:
            t.cannotSelectPastDate ||
            "You cannot select a past date. Please choose today or a future date.",
        });
        return;
      }

      const cellStatus = String(
        cellInfo.rawPeriod?.status || cellInfo.status || "",
      ).toLowerCase();

      // #7: White cells only — reject any cell with a status (colored)
      if (cellStatus && cellStatus !== "") {
        setCellErrorModal({
          open: true,
          message:
            t.onlyAvailableCellsSelectable ||
            "You can only select available (white) periods. Cells that are already submitted, approved, rejected, re-submitted, or completed cannot be selected.",
        });
        return;
      }

      const key = `${cellInfo.date}_${cellInfo.periodId}`;
      const idx = selectedCells.findIndex(
        (c) => `${c.date}_${c.periodId}` === key,
      );
      // Toggle off if same exact cell clicked again
      if (idx !== -1) {
        setSelectedCells((prev) => prev.filter((_, i) => i !== idx));
        return;
      }

      const cellSubject =
        cellInfo.rawPeriod?.subject_name || cellInfo.subject || "";
      const cellClassId = cellInfo.rawPeriod?.class_id
        ? String(cellInfo.rawPeriod.class_id)
        : "";
      const cellSectionId = cellInfo.rawPeriod?.section_id
        ? String(cellInfo.rawPeriod.section_id)
        : "";
      const cellMedium = cellInfo.rawPeriod?.medium || cellInfo.medium || "";

      // ✅ NEW: medium must match the selected medium (EM vs TA, etc.)
      if (
        lockedMediumForCells &&
        cellMedium &&
        normalize(cellMedium) !== normalize(lockedMediumForCells)
      ) {
        setCellErrorModal({
          open: true,
          message:
            (t.invalidMediumSelection ||
              "You can only select periods with the same medium") +
            ` (${lockedMediumForCells}).`,
        });
        return;
      }

      if (
        lockedSubjectForCells &&
        cellSubject &&
        normalize(cellSubject) !== normalize(lockedSubjectForCells)
      ) {
        setCellErrorModal({
          open: true,
          message:
            (t.invalidSubjectSelection ||
              "You can only select periods with the same subject") +
            ` (${lockedSubjectForCells}).`,
        });
        return;
      }

      if (
        lockedClassForCells &&
        cellClassId &&
        String(cellClassId) !== String(lockedClassForCells)
      ) {
        setCellErrorModal({
          open: true,
          message:
            t.invalidClassSelection ||
            "You can only select periods with the same class.",
        });
        return;
      }

      // NEW #1: Block if same subject + class + section combo is already selected
      const duplicateCombo = selectedCells.some((c) => {
        const existingSubject = c.rawPeriod?.subject_name || c.subject || "";
        const existingClassId = c.rawPeriod?.class_id
          ? String(c.rawPeriod.class_id)
          : "";
        const existingSectionId = c.rawPeriod?.section_id
          ? String(c.rawPeriod.section_id)
          : "";
        return (
          normalize(existingSubject) === normalize(cellSubject) &&
          existingClassId === cellClassId &&
          existingSectionId === cellSectionId
        );
      });

      if (duplicateCombo) {
        const cellClassName = cellInfo.rawPeriod?.class_name || "";
        const comboLabel = `${cellSubject} ${cellClassName}${
          cellSectionId ? ` - ${cellSectionId}` : ""
        }`.trim();
        setCellErrorModal({
          open: true,
          message:
            t.duplicateSectionSelection ||
            `You have already selected ${comboLabel}. You can select a different section of the same class, but not the same section twice.`,
        });
        return;
      }

      setSelectedCells((prev) => [...prev, cellInfo]);
    },
    [
      selectedCells,
      lockedSubjectForCells,
      lockedClassForCells,
      lockedMediumForCells,
      formData.book,
      savedLessonPlans,
      t,
    ],
  );

  /* ------------------------------------------------------------------- */
  /* Validation                                                          */
  /* ------------------------------------------------------------------- */

  const validateForGenerate = useCallback(() => {
    const tempErrors = {};
    if (!formData.book) tempErrors.book = t.bookRequired;
    if (!formData.topic) tempErrors.topic = t.topicRequired;
    if (!formData.startPage) tempErrors.startPage = t.startPageRequired;
    if (!formData.endPage) tempErrors.endPage = t.endPageRequired;

    const { min, max } = pageBounds;
    const s = parseInt(formData.startPage, 10);
    const en = parseInt(formData.endPage, 10);

    if (!Number.isNaN(s)) {
      if (min != null && s < min)
        tempErrors.startPage = `${t.pageBetween} ${min} ${t.andLabel} ${max}`;
      if (max != null && s > max)
        tempErrors.startPage = `${t.pageBetween} ${min} ${t.andLabel} ${max}`;
    }
    if (!Number.isNaN(en)) {
      if (min != null && en < min)
        tempErrors.endPage = `${t.pageBetween} ${min} ${t.andLabel} ${max}`;
      if (max != null && en > max)
        tempErrors.endPage = `${t.pageBetween} ${min} ${t.andLabel} ${max}`;
    }
    if (!Number.isNaN(s) && !Number.isNaN(en) && s > en) {
      tempErrors.endPage = t.endPageAfterStart;
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  }, [formData, pageBounds, t]);

  /* ------------------------------------------------------------------- */
  /* Generate                                                            */
  /* ------------------------------------------------------------------- */

  const buildGeneratePayload = useCallback(() => {
    const info = formData.book?.info || {};
    return {
      def_id: lessonPlanDefId || 0,
      school_id: schoolId,
      academic_id: academic_id,
      book_id: formData.book.value,
      subject: info.subject_id ? parseInt(info.subject_id, 10) : info.subject,
      class: info.class_id ? parseInt(info.class_id, 10) : info.class,
      medium: formData.medium,
      topic: formData.topic.value,
      sub_topic: formData.subTopic?.value || "",
      start_page: parseInt(formData.startPage, 10),
      end_page: parseInt(formData.endPage, 10),
      book_path: info.filePath,
      user_id: userId,
      schedule_date: scheduleDate ? formatYmd(scheduleDate) : "",
      generated_count: regenerateUsed + 1,
    };
  }, [
    formData,
    schoolId,
    academic_id,
    userId,
    scheduleDate,
    lessonPlanDefId,
    regenerateUsed,
  ]);

  const handleGenerate = useCallback(async () => {
    if (!validateForGenerate()) return;
    if (regenerateExhausted) return;

    try {
      setGenerating(true);
      setLessonPlanContent("");
      const res = await AxiosInstance.post(
        "/book/create-lessonPlan",
        buildGeneratePayload(),
      );
      if (res.data?.status) {
        const data = res.data;
        setLessonPlanContent(data.content || "");
        if (data.def_id) setLessonPlanDefId(data.def_id);

        const apiVersion = data.version ?? data.data?.version ?? null;
        const nextClickCount = regenerateUsed + 1;
        setCurrentVersion(
          apiVersion != null && apiVersion !== "" ? apiVersion : nextClickCount,
        );

        if (!pdfUrl) {
          applyPdfUrl(formData.book?.info?.filePath || "");
        }
        setHasGenerated(true);
        setRegenerateUsed(nextClickCount);
        setEditingCardHmPk(null);

        // ───── Generate/Re-Generate success
        //      → re-enable Save-as-Draft and Submit-to-HM buttons
        setDraftSaved(false);
        setSaveCompleted(false);
      } else {
        // Keep the def id so CANCEL can discard the stored defs row
        if (res.data?.def_id) setLessonPlanDefId(res.data.def_id);
        setModalState({
          open: true,
          type: "error",
          message: res.data?.message || t.failedToGenerateLessonPlan,
          redirectPath: "",
        });
      }
    } catch (err) {
      console.error("Error generating lesson plan:", err);
      setModalState({
        open: true,
        type: "error",
        message: err?.response?.data?.message || t.somethingWentWrongGenerate,
        redirectPath: "",
      });
    } finally {
      setGenerating(false);
    }
  }, [
    validateForGenerate,
    buildGeneratePayload,
    regenerateExhausted,
    regenerateUsed,
    formData.book?.info?.filePath,
    pdfUrl,
    applyPdfUrl,
    t,
  ]);

  /* ------------------------------------------------------------------- */
  /* Save                                                                */
  /* ------------------------------------------------------------------- */
  const buildSavePayload = useCallback(
    (status) => {
      const info = formData.book?.info || {};
      const first = selectedCells[0];

      const classIdFromCell = first?.rawPeriod?.class_id
        ? Number(first.rawPeriod.class_id)
        : info.class_id
          ? Number(info.class_id)
          : null;

      // course_id = subject_id from the selected book dropdown.
      // Fallback chain: book.info.subject_id → book.info.subject (numeric)
      //               → cell course_id → null.
      const subjectIdFromBook =
        info.subject_id != null && info.subject_id !== ""
          ? Number(info.subject_id)
          : null;

      const subjectFallbackNumeric =
        info.subject != null && !Number.isNaN(Number(info.subject))
          ? Number(info.subject)
          : null;

      const courseIdFromCell = first?.rawPeriod?.course_id
        ? Number(first.rawPeriod.course_id)
        : null;

      const courseId =
        subjectIdFromBook ?? subjectFallbackNumeric ?? courseIdFromCell ?? null;

      const selected_periods = selectedCells.map((c) => ({
        date: c.rawDate || c.date,
        section_id: c.rawPeriod?.section_id || "",
        period: Number(c.rawPeriod?.period || c.periodId),
      }));

      return {
        id: "",
        lesson_plan_id: lessonPlanDefId || "",
        school_id: schoolId,
        staff_id: staffId,
        version: currentVersion || regenerateUsed || 1,
        content: lessonPlanContent || "",
        start_page: parseInt(formData.startPage, 10),
        end_page: parseInt(formData.endPage, 10),
        status,
        class_id: classIdFromCell,
        course_id: courseId,
        selected_periods,
        schedule_date: scheduleDate ? formatYmd(scheduleDate) : "",
        created_by: userId,
        // item_id: lessonPlan.id,
      };
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formData,
      selectedCells,
      editingCardHmPk,
      lessonPlanDefId,
      schoolId,
      staffId,
      lessonPlanContent,
      scheduleDate,
      userId,
      currentVersion,
      regenerateUsed,
    ],
  );

  const handleSave = useCallback(
    async (mode /* 'draft' | 'submit' */) => {
      if (!contentReady) {
        setModalState({
          open: true,
          type: "error",
          message:
            t.selectLessonPlanFirst || "Please generate a lesson plan first.",
          redirectPath: "",
        });
        return;
      }

      if (mode === "submit") {
        if (!scheduleDate) {
          setErrors((prev) => ({
            ...prev,
            scheduleDate: t.scheduleDateRequired,
          }));
          setModalState({
            open: true,
            type: "error",
            message: t.scheduleDateRequired || "Schedule date is required",
            redirectPath: "",
          });
          return;
        }
        if (!selectedCells.length) {
          setModalState({
            open: true,
            type: "error",
            message:
              t.selectAtLeastOneCell ||
              "Please select at least one timetable cell.",
            redirectPath: "",
          });
          return;
        }
      }

      const status = mode === "submit" ? 1 : 0;
      const payload = buildSavePayload(status);

      try {
        setSubmitting(true);
        const res = await AxiosInstance.post("/book/lessonPlan-save", payload);
        if (res.data?.status) {
          if (mode === "draft") setDraftSaved(true);

          // Requirement #3: clear selected cells on success
          setSelectedCells([]);

          // Requirement #4: bump save count
          setSaveCount((prev) => prev + 1);

          // ───── Save success (draft OR submit)
          //      → disable BOTH Save-as-Draft and Submit-to-HM buttons
          setSaveCompleted(true);

          setModalState({
            open: true,
            type: "success",
            message:
              res.data.message ||
              (mode === "submit"
                ? t.lessonPlanScheduledSuccess
                : t.lessonPlanCreatedSuccess),
            redirectPath: "",
          });
          await fetchLessonPlanView(lessonPlanDefId);
          setEditingCardHmPk(null);
        } else {
          setModalState({
            open: true,
            type: "error",
            message: res.data?.message || t.failedToSaveLessonPlan,
            redirectPath: "",
          });
        }
      } catch (err) {
        console.error("Error saving lesson plan:", err);
        setModalState({
          open: true,
          type: "error",
          message: err?.response?.data?.message || t.somethingWentWrongSave,
          redirectPath: "",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      contentReady,
      scheduleDate,
      selectedCells,
      buildSavePayload,
      fetchLessonPlanView,
      lessonPlanDefId,
      t,
    ],
  );

  /* Update (edit mode) */
  const handleUpdate = useCallback(async () => {
    if (!contentReady) {
      setModalState({
        open: true,
        type: "error",
        message:
          t.selectLessonPlanFirst || "Please generate a lesson plan first.",
        redirectPath: "",
      });
      return;
    }

    const itemIdToSend =
      editItemId ??
      selectedCardId ??
      currentItemId ??
      (savedLessonPlans.length > 0 ? savedLessonPlans[0].id : null);

    if (itemIdToSend == null) {
      setModalState({
        open: true,
        type: "error",
        message:
          t.selectLessonPlanFirst ||
          "Please select a lesson plan to update first.",
        redirectPath: "",
      });
      return;
    }

    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append("id", itemIdToSend);
      fd.append("school_id", schoolId);
      fd.append("content", lessonPlanContent || "");

      const res = await AxiosInstance.put("/book/lessonPlan/edit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.status) {
        // Requirement #3: clear cells on success
        setSelectedCells([]);
        setSaveCount((prev) => prev + 1);

        // ───── Update success (edit-mode Primary button)
        //      → disable BOTH footer buttons
        setSaveCompleted(true);

        setModalState({
          open: true,
          type: "success",
          message: res.data.message || t.lessonPlanUpdatedSuccess,
          redirectPath: "",
        });
        await fetchLessonPlanView(editDefId, { preferItemId: itemIdToSend });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: res.data?.message || t.failedToSaveLessonPlan,
          redirectPath: "",
        });
      }
    } catch (err) {
      console.error("Error updating lesson plan:", err);
      setModalState({
        open: true,
        type: "error",
        message: err?.response?.data?.message || t.somethingWentWrongSave,
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    contentReady,
    editItemId,
    selectedCardId,
    currentItemId,
    savedLessonPlans,
    editDefId,
    schoolId,
    lessonPlanContent,
    fetchLessonPlanView,
    t,
  ]);

  /* ------------------------------------------------------------------- */
  /* Saved card handlers                                                 */
  /* ------------------------------------------------------------------- */

  const handleCardSelect = useCallback(
    (id) => {
      setSelectedCardId(id);
      const plan = savedLessonPlans.find((lp) => lp.id === id);
      if (plan) {
        setLessonPlanContent(plan.content || "");
        setCurrentVersion(plan.version || null);
        setCurrentItemId(plan.id || null);
      }
    },
    [savedLessonPlans],
  );

  const handleCardPin = useCallback((id) => {
    setSavedLessonPlans((prev) =>
      prev.map((lp) =>
        lp.id === id
          ? { ...lp, is_pinned: !lp.is_pinned }
          : { ...lp, is_pinned: false },
      ),
    );
  }, []);

  const handleEditCardNavigate = useCallback(
    (plan) => {
      navigate("/user/lessonplancreate", {
        state: {
          ID: plan.lesson_plan_id,
          data: plan,
          editId: plan.id,
        },
      });
    },
    [navigate],
  );

  /* Delete: open confirm first */
  const handleCardDelete = useCallback((id) => {
    setConfirmDelete({ open: true, planId: id });
  }, []);

  const cancelDelete = () => setConfirmDelete({ open: false, planId: null });

  const confirmDeleteAction = useCallback(async () => {
    const idToDelete = confirmDelete.planId;
    if (!idToDelete) return;
    try {
      const res = await AxiosInstance.delete("/book/lessonPlan/delete", {
        data: {
          // id: Number(lessonPlanDefId || editDefId),
          id: "",
          item_id: Number(idToDelete), // plan item id (card id)
          school_id: Number(schoolId),
          deleted_by: Number(userId),
        },
      });

      setConfirmDelete({ open: false, planId: null });
      if (res.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            res.data.message || t.successfullyDeleted || "Successfully Deleted",
          redirectPath: "",
        });
        fetchLessonPlanView(lessonPlanDefId || editDefId);
      } else {
        setModalState({
          open: true,
          type: "error",
          message: res.data?.message || t.deleteFailed,
          redirectPath: "",
        });
      }
    } catch (err) {
      console.error("Delete error:", err);
      setConfirmDelete({ open: false, planId: null });
      setModalState({
        open: true,
        type: "error",
        message: err?.response?.data?.message || t.deleteFailed,
        redirectPath: "",
      });
    }
  }, [
    confirmDelete.planId,
    schoolId,
    userId,
    lessonPlanDefId,
    editDefId,
    fetchLessonPlanView,
    t,
  ]);

  const handleCardSchedule = useCallback(
    (id) => {
      const plan = savedLessonPlans.find((lp) => lp.id === id);
      if (!plan) return;
      const enrichedPlan = {
        ...plan,
        subject_name: lessonInfo?.subject_name || "",
        class_name: lessonInfo?.class || "",
        class_id: lessonInfo?.class_id ? Number(lessonInfo.class_id) : null,
        medium: lessonInfo?.medium || "",
      };
      setSchedulePopup({ open: true, lessonPlan: enrichedPlan });
    },
    [savedLessonPlans, lessonInfo],
  );

  const handleScheduleCancel = useCallback(() => {
    setSchedulePopup({ open: false, lessonPlan: null });
  }, []);

  const handleScheduleSubmit = useCallback(
    async ({ apiResponse }) => {
      setSchedulePopup({ open: false, lessonPlan: null });
      setModalState({
        open: true,
        type: "success",
        message:
          apiResponse?.message ||
          t.lessonPlanScheduledSuccess ||
          "Lesson plan scheduled successfully",
        redirectPath: "",
      });
      fetchLessonPlanView(lessonPlanDefId);
    },
    [t, fetchLessonPlanView, lessonPlanDefId],
  );

  /* ------------------------------------------------------------------- */
  /* UI helpers                                                          */
  /* ------------------------------------------------------------------- */

  /*
   *  Cancel behavior:
   *    • Edit mode  → navigate to /user/lessonplanview with
   *                    { ID: editDefId, data: editRowData } in state
   *    • Create mode → history.back()
   */
  const handleCancel = useCallback(async () => {
    // Generated but never saved (no Draft / Submit-to-HM) → a defs row
    // with no items is left behind. Ask the backend to discard it; the
    // backend keeps the def whenever it has saved items, so this is
    // safe for the fromAllAdd/prefill flows too.
    if (!isEditMode && lessonPlanDefId) {
      try {
        await AxiosInstance.post("/book/lessonPlan/discard", {
          def_id: lessonPlanDefId,
          school_id: Number(schoolId),
          deleted_by: staffId,
        });
      } catch (err) {
        console.error("Error discarding unsaved lesson plan:", err);
      }
    }
    if (isEditMode && editDefId) {
      navigate("/user/lessonplanview", {
        state: { ID: editDefId, data: editRowData },
      });
      return;
    }
    navigate(-1);
  }, [
    navigate,
    isEditMode,
    editDefId,
    editRowData,
    lessonPlanDefId,
    schoolId,
    staffId,
  ]);

  const handleModalOk = useCallback(() => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) navigate(redirectPath);
  }, [modalState, navigate]);

  const handleViewBook = useCallback(() => {
    // pdfUrl is already blob-aware (object URL for Azure blob books)
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
      return;
    }
    const rawPath = formData.book?.info?.filePath || "";
    if (isBlobBookPath(rawPath)) {
      AxiosInstance.get("/content/image", {
        params: { path: rawPath },
        responseType: "blob",
      })
        .then((res) => {
          const objectUrl = URL.createObjectURL(
            new Blob([res.data], { type: "application/pdf" }),
          );
          window.open(objectUrl, "_blank");
        })
        .catch((err) => console.error("Error opening book PDF:", err));
      return;
    }
    const url = resolveBookPath(rawPath);
    if (url) window.open(url, "_blank");
  }, [pdfUrl, formData.book?.info?.filePath]);

  const pdfFromPage = useMemo(
    () => parseInt(formData.startPage, 10) || undefined,
    [formData.startPage],
  );
  const pdfToPage = useMemo(
    () => parseInt(formData.endPage, 10) || undefined,
    [formData.endPage],
  );

  const classDropdownValue = useMemo(() => {
    if (!formData.book?.info) return null;
    const { class: className, class_id: classId } = formData.book.info;
    if (classId == null && !className) return null;
    return {
      label: className || "",
      value: classId != null ? String(classId) : className,
    };
  }, [formData.book]);

  const mediumDropdownValue = useMemo(() => {
    if (!formData.medium) return null;
    return { label: formData.medium, value: formData.medium };
  }, [formData.medium]);

  const generateLabel = generating
    ? t.generatingLessonPlan || "Generating..."
    : hasGenerated
      ? `${t.regenerateLessonPlan || "Re-Generate"} ${regenerateUsed}/${MAX_REGENERATE}`
      : t.generateLessonPlan || "Generate Lesson Plan";

  /* Footer button logic */
  const showSaveAsDraft = !isEditMode && editingCardHmPk == null;

  /*
   *  Both footer buttons are disabled when any of these are true:
   *    • no content yet (no generated lesson plan)
   *    • request in flight (submitting / generating)
   *    • a save has completed since the last generate (saveCompleted)
   *    • 3-save hard limit hit (saveLimitReached)
   *    • (draft only) this specific draft has already been saved (draftSaved)
   */
  const saveAsDraftDisabled =
    !contentReady ||
    submitting ||
    generating ||
    draftSaved ||
    saveCompleted ||
    saveLimitReached;

  const submitDisabled =
    isEditMode || editingCardHmPk != null
      ? false
      : !contentReady ||
        submitting ||
        generating ||
        saveCompleted ||
        saveLimitReached;

  const primaryLabel =
    isEditMode || editingCardHmPk != null
      ? t.update || "Update"
      : t.submitToHM || "Submit To HM";

  /* ------------------------------------------------------------------- */
  /* Render                                                              */
  /* ------------------------------------------------------------------- */

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? t.modalSuccessTitle
            : t.modalFailedTitle
        }
        message={modalState.message}
        onOk={handleModalOk}
      />

      <PopupModal
        open={cellErrorModal.open}
        title={t.modalFailedTitle || "Invalid Selection"}
        message={cellErrorModal.message}
        onOk={() => setCellErrorModal({ open: false, message: "" })}
        onClose={() => setCellErrorModal({ open: false, message: "" })}
      />

      <PopupModal
        open={confirmDelete.open}
        title={t.delete || "Confirm Delete"}
        message={
          t.confirmDeleteMessage ||
          "Are you sure you want to delete this lesson plan?"
        }
        onOk={confirmDeleteAction}
        onClose={cancelDelete}
        okText={t.yes || "Yes"}
        cancelText={t.no || "No"}
        showCancel
      />

      <PopupModal
        open={schedulePopup.open}
        title={t.scheduleLessonPlan || "Schedule Lesson Plan"}
        onClose={handleScheduleCancel}
        wide
        render
        renderData={
          <ScheduleLessonPlanContent
            lessonPlan={schedulePopup.lessonPlan}
            lessonInfo={lessonInfo}
            onCancel={handleScheduleCancel}
            onSubmit={handleScheduleSubmit}
          />
        }
      />

      {/* ============ TOP: Form + Timetable + Generate ============ */}
      <LessonPlanFormPanel
        t={t}
        isEditMode={isEditMode}
        formLocked={formLocked}
        formData={formData}
        errors={errors}
        bookOptions={bookOptions}
        topicOptions={topicOptions}
        subTopicOptions={subTopicOptions}
        sectionDropdownOptions={sectionDropdownOptions}
        classDropdownValue={classDropdownValue}
        mediumDropdownValue={mediumDropdownValue}
        pageBounds={pageBounds}
        onBookChange={handleBookChange}
        onSectionChange={handleSectionChange}
        onTopicChange={handleTopicChange}
        onSubTopicChange={handleSubTopicChange}
        onPageInputChange={handlePageInputChange}
        onPageInputBlur={handlePageInputBlur}
        onViewBook={handleViewBook}
        scheduleDate={scheduleDate}
        onScheduleDateChange={handleScheduleDateChange}
        timetableLoading={timetableLoading}
        timetableRows={timetableRows}
        timetablePeriods={timetablePeriods}
        scheduleLegendItems={scheduleLegendItems}
        selectedCellsForTable={selectedCellsForTable}
        onCellClick={handleCellClick}
        onGenerate={handleGenerate}
        generating={generating}
        generateLabel={generateLabel}
        regenerateExhausted={regenerateExhausted}
        academic_year={academic_year}
        academic_end_date={academic_end_date}
        academic_start_date={academic_start_date}
      />

      {/* ============ MIDDLE + BOTTOM: PDF + Editor + Cards + Footer ============ */}
      {showOutputPanels && (
        <LessonPlanOutputPanel
          t={t}
          isEditMode={isEditMode}
          editingCardHmPk={editingCardHmPk}
          pdfUrl={pdfUrl}
          pdfFromPage={pdfFromPage}
          pdfToPage={pdfToPage}
          lessonPlanContent={lessonPlanContent}
          onLessonContentChange={(html) => setLessonPlanContent(html)}
          generating={generating}
          savedLessonPlans={savedLessonPlans}
          selectedCardId={selectedCardId}
          disableScheduleGlobally={disableScheduleGlobally}
          onCardSelect={handleCardSelect}
          onCardPin={handleCardPin}
          onEditCard={handleEditCardNavigate}
          onDeleteCard={handleCardDelete}
          onScheduleCard={handleCardSchedule}
          onCancel={handleCancel}
          onSaveDraft={() => handleSave("draft")}
          onPrimary={() => (isEditMode ? handleUpdate() : handleSave("submit"))}
          showSaveAsDraft={showSaveAsDraft}
          saveAsDraftDisabled={saveAsDraftDisabled}
          submitDisabled={submitDisabled}
          primaryLabel={primaryLabel}
        />
      )}
    </>
  );
};

export default LessonPlanCreate;
